import ITask from './ITask';
import {
    MapScroll,
    OverlayFadeIn,
    OverlayFadeOut,
} from '../../System/Animation/Clip';
import {
    PlayAnimation,
    StartAnimationClip,
} from '../../System/Animation/AnimationHelper';
import WorldSystem from '../../System/World/WorldSystem';
import GameObject, { GameObjectEvent } from '../../GameObject/GameObject';
import { RenderSystemEvent } from '../../System/Render/RenderSystem';
import OverlayComponent from '../../Component/UI/Overlay';
import Game from '../../Game';
import PIXIRenderer from 'System/Render/PIXIRenderer';
import TransformComponent from 'Component/Transform';
import Renderable from 'Component/Renderable';
import Overlay from 'Prototype/UI/Overlay';
import Element from 'Prototype/UI/Element';
import { TextAlignment } from 'Prototype/UI/TextAlignment';
import { MainAnim } from 'System/Animation/Clip/MainAnim';
import { delay } from './TaskQueue';
import { PlayerActions } from 'State/Player/PlayerActions';
import { FTUXType } from 'Player/PlayerProperty/Progress/FTUXType';

export default class WheelFTUXTask implements ITask<WorldSystem> {
    private readonly firstWheelLevel: number;
    public closeLuckyWheelFTUX: boolean;

    constructor(firstWheelLevel: number) {
        this.firstWheelLevel = firstWheelLevel;
    }

    public *execute(system: WorldSystem): IterableIterator<any> {
        this.closeLuckyWheelFTUX = false;

        const luckyWheelsContainers = system.worldMap.luckyWheelContainer;
        const firstLuckyWheel = luckyWheelsContainers.findChildByName(`luckyWheel_${this.firstWheelLevel}`);

        const luckyWheelLevelNode = system.getLevelNode(this.firstWheelLevel);

        // https://sentry.io/organizations/softgames-solitaire-farm-seasons/issues/2432987587/?project=1856050&query=is%3Aunresolved&statsPeriod=7d
        if (!luckyWheelLevelNode) return;

        const currentLevelNode = system.getCurrentLevelNode();

        const screenWidth: number = PIXIRenderer.mapViewport.screenWidth;

        // Get positions for scrolling
        const luckyWheelPosition = luckyWheelLevelNode
            .getComponent(TransformComponent)
            .position.clone();
        const currentNodePosition = currentLevelNode
            .getComponent(TransformComponent)
            .position.clone();

        // Center in screen
        luckyWheelPosition.x = -(luckyWheelPosition.x - screenWidth / 2);
        currentNodePosition.x = -(currentNodePosition.x - screenWidth / 2);

        // Create scroll clips
        const maxTime = 4000;
        const scrollToLuckyWheelClip = new MapScroll(luckyWheelLevelNode, {
            position: luckyWheelPosition,
            maxTime,
        });
        const scrollToCurrentNodeClip = new MapScroll(currentLevelNode, {
            position: currentNodePosition,
            maxTime,
        });

        const overlay: Overlay = GameObject.Instantiate(Overlay, {
            name: 'pet_ftux_overlay',
            preventInteraction: true,
            anchors: [0.5, 0.5, 0.5, 0.5],
            alpha: 0,
            color: [0, 0, 0, 225],
        });

        system.animator.addChild(overlay);
        system.animator.scene.emit(
            RenderSystemEvent.BRING_TO_FRONT,
            system.animator
        );

        overlay.once(GameObjectEvent.POINTER_UP, () => { this.closeLuckyWheelFTUX = true });

        const background = GameObject.Instantiate(Element, {
            name: 'background',
            layout: {
                anchors: [0.3, 0.45, 0.3, 0.45],
                pivot: [0.5, 0.5],
                offsets: [-75, 0, -75, 0],
                scale: [1, 1],
            },
            children: [
                {
                    name: 'bgtest',
                    layout: {
                        offsets: [0, 22, 0, 22],
                        constrains: {
                            widthMax: 510,
                            heightMax: 275,
                        },
                    },
                    components: [
                        {
                            type: 'nineslice',
                            data: {
                                cacheKey: 'mission_board',
                                offset: [80, 80, 80, 80],
                            },
                        },
                    ]
                },
                {
                    name: 'text',
                    layout: {
                        anchors: [0.5, 0.45, 0.5, 0.45],
                        pivot: [0.5, 0.5],
                        offsets: [0, 0, 0, 0],
                    },
                    components: [
                        {
                            type: 'text',
                            data: {
                                style: Game.style.getStyle('luckyWheel.FTUX'),
                                text: Game.locale.getText('LuckyWheelFTUX'),
                                bounds: [400, 300],
                                wordWrap: true,
                                wordWrapWidth: 400,
                                align: TextAlignment.CENTER,
                            },
                        },
                    ],
                },
            ],
        });

        const tempLuckyWheel = GameObject.Instantiate(Element, {
            name: 'level_node',
            layout: {
                anchors: [0.6, 0.65, 0.6, 0.65],
                pivot: [0.5, 0.5],
                offsets: [0, 110, 0, 110],
                scale: [1, 1],
            },
            components: [
                {
                    type: 'image',
                    data: {
                        cacheKey: 'node_current',
                    },
                },
            ],
            children: [
                {
                    name: 'lucky_wheel_body',
                    layout: {
                        anchors: [0.5, 0, 0.5, 0],
                        pivot: [0.5, 0.5],
                        offsets: [0, 15, 0, 15],
                    },
                    components: [
                        {
                            type: 'image',
                            data: {
                                cacheKey: 'wheel_body',
                            },
                        },
                    ],
                    children: [
                        {
                            name: 'lucky_wheel_needle',
                            layout: {
                                anchors: [0.5, 0.5, 0.5, 0.5],
                                pivot: [0.44, 0.55],
                                offsets: [0, 15, 0, 15],
                            },
                            components: [
                                {
                                    type: 'image',
                                    data: {
                                        cacheKey: 'wheel_needle',
                                    },
                                },
                            ],
                        }
                    ]
                },
            ],
        });

        overlay.addChild(background);
        overlay.addChild(tempLuckyWheel);

        overlay.getComponent(OverlayComponent).preventInteraction = true;

        firstLuckyWheel.getComponent(Renderable).alpha = 0;
        firstLuckyWheel.getComponent(Renderable).visible = true;

        yield* PlayAnimation(scrollToLuckyWheelClip);
        yield* PlayAnimation(new MainAnim(firstLuckyWheel, {alpha: 1, duration: 500}));

        yield* delay(300);

        StartAnimationClip(new OverlayFadeIn(overlay));
        yield* PlayAnimation(new MainAnim(firstLuckyWheel, {alpha: 0, duration: 500}));

        while (!this.closeLuckyWheelFTUX) {
            yield;
        }

        StartAnimationClip(new OverlayFadeOut(overlay));
        yield* PlayAnimation(new MainAnim(firstLuckyWheel, {alpha: 1, duration: 500}));

        yield* PlayAnimation(scrollToCurrentNodeClip);

        luckyWheelsContainers.children.forEach(container => {
            container.getComponent(Renderable).visible = true;
            container.getComponent(Renderable).alpha = 1;
        });

        overlay.getComponent(OverlayComponent).preventInteraction = false;
        background.destroy();
        overlay.destroy();

        PlayerActions.setFTUXUnlocks([FTUXType.LUCKY_WHEEL]);
    }
}
